<template>
  <StakeModal
    v-model="isModalVisible"
    fullwidth
    confirm-message="Are you sure you want to cancel?"
    :need-to-confirm="hasChanged"
  >
    <Stack class="stk-p-4">
      <Title>{{ title }}</Title>

      <slot />

      <Callout v-if="apiErrors.length" status="danger">
        <div class="content api-errors">
          <p>There is an error:</p>
          <ul>
            <li v-for="error in apiErrors" :key="error" class="api-error">
              {{ error }}
            </li>
          </ul>
        </div>
      </Callout>

      <Stack
        v-for="field in fields"
        :key="field.id"
        gap="1"
      >
        <Label :for="field.id">{{ field.label }}</Label>

        <FormText
          v-if="field.type === 'text'"
          :id="field.id"
          v-model="formData[field.id]"
          :type="field.inputType"
        />

        <FormEmail
          v-else-if="field.type === 'email'"
          :id="field.id"
          v-model="formData[field.id]"
        />

        <FormDate
          v-else-if="field.type === 'date'"
          :id="field.id"
          v-model="formData[field.id]"
        />

        <FormCheckboxes
          v-else-if="field.type === 'checkboxes'"
          :id="field.id"
          :name="field.id"
          v-model="formData[field.id]"
          :options="field.options"
        />

        <FormRadios
          v-else-if="field.type === 'radios'"
          :id="field.id"
          :name="field.id"
          v-model="formData[field.id]"
          :options="field.options"
        />

        <FormSelect
          v-else-if="field.type === 'select'"
          :id="field.id"
          v-model="formData[field.id]"
          :options="field.options"
        />

        <FormList
          v-else-if="field.type === 'list'"
          :id="field.id"
          v-model="formData[field.id]"
        />

        <FormEntity
          v-else-if="field.type === 'entity'"
          :id="field.id"
          v-model="formData[field.id]"
        />

        <div v-else>
          Not supported
        </div>
      </Stack>

      <Buttons justify="between">
        <Buttons>
          <Button
            variant="success"
            :disabled="isSaving || !hasChanged"
            @click="handleSave"
          >
            Save Changes
          </Button>
        </Buttons>

        <Button
          variant="danger"
          :disabled="isSaving"
          @click="handleCancel"
        >
          Cancel
        </Button>
      </Buttons>
    </Stack>
  </StakeModal>
</template>

<script setup>
import Vue from 'vue';
import { defineEmits, watch, computed, reactive, ref } from 'vue';
import { ToastProgrammatic as Toast } from 'buefy';

import { Buttons, Button, Callout, Label, Title, Stack, StakeModal } from '@stakeui';
import FormCheckboxes from './FormCheckboxes.vue';
import FormDate from './FormDate.vue';
import FormEmail from './FormEmail.vue';
import FormEntity from './FormEntity.vue';
import FormList from './FormList.vue';
import FormRadios from './FormRadios.vue';
import FormSelect from './FormSelect.vue';
import FormText from './FormText.vue';

const props = defineProps({
  endpoint: {
    type: String,
    required: true,
  },
  fields: {
    type: Array,
    required: true,
    default: () => ([]),
  },
  title: {
    type: String,
    required: true,
  },
  value: {
    type: Boolean,
    default: false,
  },
  apiMode: {
    type: String,
    default: 'json',
  },
  changeType: {
    type: String,
    default: 'update'
  },
});

const emit = defineEmits(['input']);

// State
const formData = reactive({});
const isSaving = ref(false);
const hasChanged = ref(false);
const apiErrors = ref([]);

let isInitialized = false;

// Functions
const initializeFormData = (fields = []) => {
  // Build the next shape
  const next = {};
  for (const field of fields) {
    next[field.id] = field.value ?? '';
  }

  // Remove keys that no longer exist
  for (const key in formData) {
    if (!(key in next)) {
      Vue.delete(formData, key);
    }
  }

  // Add/update keys reactively
  for (const [key, val] of Object.entries(next)) {
    Vue.set(formData, key, val);
  }
};

const resetForm = () => {
  initializeFormData(props.fields);
  hasChanged.value = false;
};

const handleSave = () => {
  saveEdits();
  hasChanged.value = false;
};

const handleCancel = () => {
  if (hasChanged.value) {
    if (window.confirm('Are you sure you want to cancel?')) {
      resetForm();
      emit('input', false); // close modal
    }
  } else {
    // no changes → just close
    emit('input', false);
  }
};

// Async Functions
const saveEdits = async () => {
  if (isSaving.value) {
    return;
  }

  isSaving.value = true;

  try {
    let body;
    const headers = {
      'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]')?.value,
    };

    if (props.apiMode === 'json') {
      headers['Content-Type'] = 'application/json';
      const payload = JSON.parse(JSON.stringify(formData));
      body = JSON.stringify(payload);
    } else if (props.apiMode === 'form') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
      body = new URLSearchParams(formData);
    }

    const response = await fetch(props.endpoint, {
      method: 'POST',
      headers,
      body,
    });

    if (!response.ok) {
      const data = await response.json();

      if (data.errors) {
        apiErrors.value = Object.values(data.errors || {})
          .flat()
          .map(e => e.message);
      }

      throw new Error(`Failed to apply the ${props.changeType}`);
    }

    apiErrors.value = [];

    Toast.open({
      message: `The ${props.changeType} was successful.`,
      type: 'is-success',
    });
  } catch (error) {
    console.error(`Error with the ${props.changeType}:`, error);

    if (error.errors) {
      apiErrors.value = Object.values(error.errors)
        .flat()
        .map(e => e.message);
    }

    Toast.open({
      message: `There was an error with the ${props.changeType}.`,
      type: 'is-danger',
    });
  } finally {
    isSaving.value = false;
  }
};

// Computed
const isModalVisible = computed({
  get: () => props.value,
  set: val => emit('input', val),
});

watch(
    () => props.fields,
    newFields => {
      initializeFormData(newFields);
    },
    { immediate: true },
);

watch(
    formData,
    () => {
      if (!isInitialized) {
        // first run, just mark initialized
        isInitialized = true;
        return;
      }
      hasChanged.value = true;
    },
    { deep: true },
);
</script>

<style scoped>
.api-errors {
  font-size: 0.875em;
  padding: 0.5em;
}

.api-error {
  word-break: break-word;
}
</style>
